import React from 'react'
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2,
  Target,
  Users,
  BarChart3,
  Lightbulb,
  PieChart,
  Calculator,
  TrendingDown,
  AlertTriangle
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Checkbox } from '../../../components/ui/Checkbox'
import { Textarea } from '../../../components/ui/Textarea'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { RevenueStream } from '../../../types'
import { cn } from '../../../lib/utils'

export const RevenueIntelligenceScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()
  
  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Industry Selection Required</h3>
          <p className="text-gray-500">Please select an industry first to configure your revenue intelligence.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  const addRevenueStream = (type: string) => {
    const existingStream = data.revenueStreams.find(stream => stream.type === type)
    if (!existingStream) {
      const newStream: RevenueStream = {
        type,
        percentage: 0,
        partners: [],
        notes: ''
      }
      updateData('revenueStreams', [...data.revenueStreams, newStream])
    }
  }

  const updateRevenueStream = (type: string, field: keyof RevenueStream, value: any) => {
    const updatedStreams = data.revenueStreams.map(stream => 
      stream.type === type ? { ...stream, [field]: value } : stream
    )
    updateData('revenueStreams', updatedStreams)
  }

  const removeRevenueStream = (type: string) => {
    const filteredStreams = data.revenueStreams.filter(stream => stream.type !== type)
    updateData('revenueStreams', filteredStreams)
  }

  const togglePartner = (streamType: string, partner: string) => {
    const stream = data.revenueStreams.find(s => s.type === streamType)
    if (!stream) return

    const currentPartners = stream.partners || []
    const updatedPartners = currentPartners.includes(partner)
      ? currentPartners.filter(p => p !== partner)
      : [...currentPartners, partner]
    
    updateRevenueStream(streamType, 'partners', updatedPartners)
  }

  const getTotalPercentage = () => {
    return data.revenueStreams.reduce((total, stream) => total + stream.percentage, 0)
  }

  const isPercentageValid = () => {
    const total = getTotalPercentage()
    return total <= 100
  }

  const getCompletionStatus = () => {
    const totalPercentage = getTotalPercentage()
    const hasValidPercentages = isPercentageValid()
    const hasStreams = data.revenueStreams.length > 0
    
    if (!hasStreams) return { status: 'none', message: 'No revenue streams configured' }
    if (!hasValidPercentages) return { status: 'error', message: 'Percentages exceed 100%' }
    if (totalPercentage < 80) return { status: 'partial', message: 'Configuration in progress' }
    if (totalPercentage < 100) return { status: 'good', message: 'Nearly complete' }
    return { status: 'complete', message: 'Revenue configuration complete' }
  }

  // Industry-specific partner options
  const getPartnerOptions = (streamType: string) => {
    if (data.selectedIndustry === 'automotive-services' && streamType === 'insurance-work') {
      return ['State Farm', 'Allstate', 'Geico', 'Progressive', 'Farmers', 'Liberty Mutual', 'USAA', 'Other']
    }
    return []
  }

  const completionStatus = getCompletionStatus()
  const totalPercentage = getTotalPercentage()

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-emerald-100 rounded-full">
          <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-emerald-700">{industryConfig.name}</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Revenue Intelligence Configuration
          <HelpTooltip text="Set up tracking and monitoring for your revenue streams. This enables us to provide alerts about market trends, pricing changes, and opportunities that directly impact your income sources." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Configure your revenue streams to receive targeted intelligence about market forces, 
          pricing trends, and opportunities that directly impact your income.
        </p>

        {/* Progress Summary */}
        <div className="mt-8 max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Revenue Coverage</span>
            <span className={cn(
              "text-sm font-bold",
              totalPercentage > 100 ? "text-red-600" : 
              totalPercentage >= 90 ? "text-green-600" : 
              totalPercentage >= 50 ? "text-yellow-600" : "text-gray-500"
            )}>
              {totalPercentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={cn(
                "h-3 rounded-full transition-all duration-500",
                totalPercentage > 100 ? "bg-gradient-to-r from-red-500 to-red-600" :
                totalPercentage >= 90 ? "bg-gradient-to-r from-green-500 to-green-600" :
                totalPercentage >= 50 ? "bg-gradient-to-r from-yellow-500 to-yellow-600" :
                "bg-gradient-to-r from-gray-400 to-gray-500"
              )}
              style={{ width: `${Math.min(totalPercentage, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Revenue Stream Selection */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            Revenue Stream Types
          </CardTitle>
          <CardDescription className="text-base">
            Select the revenue streams that apply to your business for targeted market intelligence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {industryConfig.revenueStreamTypes.map((streamType) => {
              const isSelected = data.revenueStreams.some(stream => stream.type === streamType.id)
              
              return (
                <Card 
                  key={streamType.id}
                  className={cn(
                    "cursor-pointer transition-all duration-300 hover:shadow-lg border-2 relative overflow-hidden",
                    isSelected 
                      ? "border-emerald-500 bg-emerald-50 shadow-lg" 
                      : "border-gray-200 hover:border-emerald-300"
                  )}
                  onClick={() => {
                    if (isSelected) {
                      removeRevenueStream(streamType.id)
                    } else {
                      addRevenueStream(streamType.id)
                    }
                  }}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 fill-emerald-100" />
                    </div>
                  )}

                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                          isSelected 
                            ? "bg-emerald-100 text-emerald-600" 
                            : "bg-gray-100 text-gray-500"
                        )}>
                          <DollarSign className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg mb-2">{streamType.name}</div>
                          <p className="text-sm text-gray-600 leading-relaxed">{streamType.description}</p>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-blue-800 mb-1">Intelligence Value</div>
                            <div className="text-sm text-blue-700">{streamType.intelligenceValue}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Revenue Stream Configuration */}
      {data.revenueStreams.length > 0 && (
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              Revenue Stream Configuration
            </CardTitle>
            <CardDescription className="text-base">
              Configure percentage breakdown and key details for each revenue stream
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Percentage Validation Alert */}
            {!isPercentageValid() && (
              <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-red-800 mb-2">Percentage Validation Error</div>
                    <div className="text-red-700 mb-2">
                      Total percentage cannot exceed 100%. Current total: <strong>{getTotalPercentage()}%</strong>
                    </div>
                    <div className="text-sm text-red-600">
                      Please adjust the percentages to ensure they add up to 100% or less.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {data.revenueStreams.map((stream, index) => {
              const streamConfig = industryConfig.revenueStreamTypes.find(t => t.id === stream.type)
              if (!streamConfig) return null

              const partnerOptions = getPartnerOptions(stream.type)

              return (
                <Card key={stream.type} className="border-l-4 border-l-emerald-500 shadow-sm">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <span className="text-emerald-600 font-bold text-sm">{index + 1}</span>
                        </div>
                        <h4 className="font-semibold text-xl">{streamConfig.name}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <PieChart className="w-4 h-4 text-emerald-600" />
                        <span className="font-bold text-emerald-600">{stream.percentage}%</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <Label htmlFor={`percentage-${stream.type}`} className="flex items-center gap-2 font-medium">
                            <Calculator className="w-4 h-4 text-gray-500" />
                            Revenue Percentage
                          </Label>
                          <div className="flex items-center gap-3">
                            <Input
                              id={`percentage-${stream.type}`}
                              type="number"
                              min="0"
                              max="100"
                              value={stream.percentage}
                              onChange={(e) => updateRevenueStream(
                                stream.type, 
                                'percentage', 
                                parseInt(e.target.value) || 0
                              )}
                              placeholder="0"
                              className="w-32 h-12 text-base text-center font-semibold"
                            />
                            <span className="text-gray-600 font-medium">% of total revenue</span>
                          </div>
                        </div>

                        {partnerOptions.length > 0 && (
                          <div className="space-y-4">
                            <Label className="flex items-center gap-2 font-medium">
                              <Users className="w-4 h-4 text-gray-500" />
                              Key Partners & Relationships
                            </Label>
                            <div className="grid grid-cols-1 gap-3">
                              {partnerOptions.map((partner) => (
                                <div key={partner} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                  <Checkbox
                                    id={`${stream.type}-${partner}`}
                                    checked={stream.partners?.includes(partner) || false}
                                    onCheckedChange={() => togglePartner(stream.type, partner)}
                                    className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                                  />
                                  <Label 
                                    htmlFor={`${stream.type}-${partner}`}
                                    className="font-medium cursor-pointer flex-1"
                                  >
                                    {partner}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            {stream.partners && stream.partners.length > 0 && (
                              <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                                <div className="flex items-start gap-3">
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                                  <div>
                                    <div className="font-semibold text-emerald-800 mb-1">Partner-Specific Intelligence</div>
                                    <div className="text-sm text-emerald-700">
                                      We'll provide targeted alerts about <strong>your specific partners</strong> - 
                                      rate changes, policy updates, and network requirements that directly impact your revenue.
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor={`notes-${stream.type}`} className="flex items-center gap-2 font-medium">
                          <Target className="w-4 h-4 text-gray-500" />
                          Additional Details
                        </Label>
                        <Textarea
                          id={`notes-${stream.type}`}
                          value={stream.notes || ''}
                          onChange={(e) => updateRevenueStream(stream.type, 'notes', e.target.value)}
                          placeholder="Describe any specific details about this revenue stream, seasonal patterns, growth plans, or challenges..."
                          rows={6}
                          className="text-base"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}

            {/* Revenue Summary */}
            <div className={cn(
              "p-6 rounded-xl border-2 transition-all duration-300",
              totalPercentage > 100 ? "bg-red-50 border-red-200" :
              totalPercentage === 100 ? "bg-green-50 border-green-200" :
              totalPercentage >= 80 ? "bg-yellow-50 border-yellow-200" :
              "bg-gray-50 border-gray-200"
            )}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    totalPercentage > 100 ? "bg-red-100" :
                    totalPercentage === 100 ? "bg-green-100" :
                    totalPercentage >= 80 ? "bg-yellow-100" :
                    "bg-gray-100"
                  )}>
                    {totalPercentage === 100 ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : totalPercentage > 100 ? (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    ) : (
                      <TrendingUp className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <span className="text-xl font-semibold">Revenue Coverage Summary</span>
                </div>
                <span className={cn(
                  "text-2xl font-bold",
                  totalPercentage > 100 ? "text-red-600" :
                  totalPercentage === 100 ? "text-green-600" :
                  totalPercentage >= 80 ? "text-yellow-600" :
                  "text-gray-600"
                )}>
                  {totalPercentage}%
                </span>
              </div>
              
              <div className={cn(
                "text-base",
                totalPercentage > 100 ? "text-red-700" :
                totalPercentage === 100 ? "text-green-700" :
                totalPercentage >= 80 ? "text-yellow-700" :
                "text-gray-700"
              )}>
                {totalPercentage > 100 && "⚠️ Total exceeds 100% - please adjust your percentages"}
                {totalPercentage === 100 && "✅ Perfect! Complete revenue breakdown configured"}
                {totalPercentage >= 80 && totalPercentage < 100 && `📊 ${100 - totalPercentage}% remaining to allocate`}
                {totalPercentage < 80 && "📈 Continue adding revenue streams to complete your configuration"}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Intelligence Preview */}
      {data.revenueStreams.length > 0 && (
        <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-blue-800 mb-3">Revenue Intelligence Monitoring Preview</h3>
              <p className="text-blue-700 mb-6">
                Based on your revenue configuration, our AI will continuously monitor and alert you about:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {data.revenueStreams.map((stream) => {
                  const streamConfig = industryConfig.revenueStreamTypes.find(t => t.id === stream.type)
                  return (
                    <div key={stream.type} className="bg-white/70 rounded-xl p-4 border border-blue-200/50">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4 text-blue-700" />
                        </div>
                        <div>
                          <div className="font-semibold text-blue-800 mb-2">
                            {streamConfig?.name} ({stream.percentage}%)
                          </div>
                          <div className="text-sm text-blue-700">
                            {streamConfig?.intelligenceValue}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-blue-200/50">
                <div className="text-sm text-blue-700 font-medium">
                  🚀 Ready for the next step? Configure your supply chain intelligence to complete your market monitoring setup.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}